<script setup lang="ts">
type Props = {
  notification: TInboxMessage;
};

const { notification } = defineProps<Props>();

const { runNotificationAction } = useSmarticoInit();

const { loadSingleMessage, markNotificationAsRead, loadMessages } =
  useSmarticoMessages();

const isReadManual = ref<boolean>(false);

const isNotificationRead = computed(() => {
  return notification.read || isReadManual.value;
});

const titleCircleColor = computed(() => {
  return isNotificationRead.value ? 'titleCircleRead' : 'titleCircleUnread';
});

const isLoadingItem = ref<boolean>(false);
const currentNotification = ref<TInboxMessageBody>();

const loadNotificationDetails = async () => {
  isLoadingItem.value = true;

  currentNotification.value = await loadSingleMessage(
    notification.message_guid,
  );

  setTimeout(() => {
    isLoadingItem.value = false;
  }, 300);
};

const markAsRead = async () => {
  if (!notification.read) {
    await markNotificationAsRead(notification.message_guid);
    isReadManual.value = true;
    loadMessages();
  }
};

const computedMessageBody = computed(() => {
  if (currentNotification.value?.html_body) {
    return currentNotification.value?.html_body;
  } else if (currentNotification.value?.body) {
    return currentNotification.value?.body;
  } else if (currentNotification.value?.preview_body) {
    return currentNotification.value?.preview_body;
  } else {
    return '';
  }
});

const actionButtonText = computed(() => {
  const text = ref('Abrir');
  const action = currentNotification.value?.action;

  if (action?.includes('missions')) {
    text.value = 'Acessar Missões';
  } else if (action?.includes('levels')) {
    text.value = 'Acessar Níveis';
  } else if (action?.includes('tournaments')) {
    text.value = 'Acessar Torneios';
  } else if (action?.includes('store')) {
    text.value = 'Acessar Loja';
  } else if (action?.includes('push')) {
    text.value = 'Liberar Notificações Push';
  } else if (action?.includes('nickname')) {
    text.value = 'Alterar Nickname';
  } else if (action?.includes('avatar')) {
    text.value = 'Alterar Avatar';
  }

  return text.value;
});

// atalhos de URL/missions/levels/tournaments/store/nickname/avatar (e o
// fallback pro SDK genérico) moraram aqui antes — agora compartilhados com o
// Minha Conta V2 em runNotificationAction (stores/smartico/init.ts).
const actionNotification = (guid: string, act: string = 'dp:ok') => {
  markNotificationAsRead(guid);
  runNotificationAction(act);

  loadMessages();
  loadNotificationDetails();
  isReadManual.value = true;
};

onMounted(async () => {
  await loadNotificationDetails();
});
</script>

<template>
  <div class="defaultItemBox notHover">
    <template v-if="isLoadingItem">
      <div :class="$style.loadingItem">
        <div
          v-for="(n, index) in 3"
          :key="`skeleton-${index}`"
          class="load skeleton-loading"
          :class="$style.skeleton"
        ></div>
      </div>
    </template>
    <template v-else>
      <div
        :class="[
          `defaultBoxContent notificationBox`,
          { [`isRead`]: isNotificationRead },
        ]"
      >
        <div class="titleBox smaller">
          <span
            :class="[titleCircleColor, 'cursor-pointer']"
            title="Marcar como lida"
            @click="markAsRead"
          ></span>
          {{ currentNotification?.title }}
        </div>

        <div
          class="descriptionBox"
          v-html="computedMessageBody"
        />
        <div class="infoBox">
          <div class="itemInfo">
            <div class="actionBox">
              <button
                v-if="
                  currentNotification?.action &&
                  currentNotification?.action !== 'dp:ok'
                "
                class="items-center justify-center inline-flex gap-1.5"
                @click="
                  actionNotification(
                    notification.message_guid,
                    currentNotification?.action,
                  )
                "
              >
                <NuxtIcon
                  class="text-sm"
                  name="open"
                />
                {{ actionButtonText }}
              </button>

              <button
                v-else-if="
                  currentNotification?.action &&
                  currentNotification?.action === 'dp:ok' &&
                  !isNotificationRead
                "
                @click="
                  actionNotification(
                    notification.message_guid,
                    currentNotification?.action,
                  )
                "
              >
                <span>{{ $t('gamification.button.mark_as_read') }}</span>
              </button>
              <button
                v-else
                class="inactive"
              >
                <span>{{ $t('gamification.button.already_viewed') }}</span>
              </button>
            </div>
          </div>
          <div class="itemInfo m-0">
            <span>{{ notification.sent_date.split(' ')[0] }}</span>
          </div>
        </div>
      </div>
      <pre
        v-if="isDev() && false"
        class="gamificationPreDebug"
        style="height: 100px"
      >
      {{ notification }}
      {{ currentNotification }}
    </pre
      >
    </template>
  </div>
</template>

<style module lang="postcss">
.loadingItem {
  @apply flex gap-3 flex-wrap p-3;
  .skeleton {
    @apply invert opacity-10 w-full;
    &:nth-child(1) {
      @apply h-[25px];
    }
    &:nth-child(2) {
      @apply h-[60px];
    }
    &:nth-child(3) {
      @apply h-[20px];
    }
  }
}
</style>
